import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, auth } from "../../lib/api";
import { useT, useLocale } from "../../lib/i18n";
import { routeReportAction } from "../../lib/report-action-router";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";

const REPORT_TYPES = ["weekly", "monthly", "campaign", "creative", "budget", "regional"];

function ConfidenceChip({ value, testid }) {
  const v = typeof value === "number" ? value : 0;
  const tone = v >= 0.8 ? "bg-emerald-100 text-emerald-900" :
                v >= 0.6 ? "bg-amber-100 text-amber-900" : "bg-rose-100 text-rose-900";
  return <span data-testid={testid} className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-mono ${tone}`}>{v.toFixed(2)}</span>;
}

function AIRunBadgeInline({ model, cost, latency_ms, confidence }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono" data-testid="report-run-badge">
      <span>{model || "—"}</span>
      <span>·</span>
      <span>${typeof cost === "number" ? cost.toFixed(4) : "—"}</span>
      <span>·</span>
      <span>{latency_ms ? `${(latency_ms / 1000).toFixed(1)}s` : "—"}</span>
      <span>·</span>
      <span>Q {typeof confidence === "number" ? confidence.toFixed(2) : "—"}</span>
    </div>
  );
}

function ReportDetail({ report, runMeta }) {
  const t = useT();
  const { locale } = useLocale();
  const nav = useNavigate();
  if (!report) return null;
  const sections = report.sections || [];
  const actions = report.recommended_actions || [];
  return (
    <div className="space-y-4" data-testid="report-detail">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-baseline justify-between">
            <CardTitle className="text-lg">{t("reports.summary_title")}</CardTitle>
            <span className="text-xs text-muted-foreground" data-testid="report-period">
              {report.period_from?.slice(0, 10)} → {report.period_to?.slice(0, 10)}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm">{report.summary}</p>
          <div className="flex items-center justify-between pt-1">
            <ConfidenceChip value={report.confidence} testid="report-confidence" />
            <AIRunBadgeInline model={runMeta?.model} cost={runMeta?.cost_usd}
              latency_ms={runMeta?.latency_ms} confidence={report.confidence} />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3" data-testid="report-sections">
        {sections.map((s, i) => (
          <Card key={i} data-testid={`report-section-${i}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">{s.question}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm whitespace-pre-line">{s.answer}</p>
              {(s.supporting_metrics || []).length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid={`report-metrics-${i}`}>
                  {s.supporting_metrics.map((m, j) => (
                    <Badge key={j} variant="secondary" className="font-mono text-[11px]">{m}</Badge>
                  ))}
                </div>
              )}
              <div className="flex justify-end">
                <ConfidenceChip value={s.confidence} testid={`report-section-confidence-${i}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {actions.length > 0 && (
        <Card data-testid="report-actions-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">{t("reports.recommended_actions")}</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {actions.map((a, i) => {
                const target = routeReportAction(a, locale);
                return (
                  <li key={i} className="flex items-start gap-2 text-sm" data-testid={`report-action-${i}`}>
                    <input type="checkbox" className="mt-1" data-testid={`report-action-check-${i}`} />
                    <span className="flex-1">{a}</span>
                    {target && (
                      <Button size="sm" variant="outline"
                        data-testid={`report-action-cta-${i}`}
                        onClick={() => nav(target.to)}>
                        {target.label}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function Reports() {
  const t = useT();
  const { locale } = useLocale();
  const [history, setHistory] = useState([]);
  const [active, setActive] = useState(null);
  const [activeRunMeta, setActiveRunMeta] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [type, setType] = useState("weekly");
  const [reportLocale, setReportLocale] = useState(locale || "de");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  // Phase 14b — Weekly Recap
  const [recapHtml, setRecapHtml] = useState(null);
  const [recapSince, setRecapSince] = useState("");
  const [recapUntil, setRecapUntil] = useState("");
  const [recapLoading, setRecapLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  // Phase 18c — PDF status pill: null (never rendered) | queued |
  // rendering | ready | failed. Once ready, asset_url lets us surface a
  // "Download again" link that hits the signed-URL asset route.
  const [pdfStatus, setPdfStatus] = useState(null);
  const [pdfAssetUrl, setPdfAssetUrl] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  // Phase 14b — Executive Summary
  const [execText, setExecText] = useState(null);
  const [execLoading, setExecLoading] = useState(false);
  const [execRunId, setExecRunId] = useState(null);

  const canWrite = auth.hasPermission("reports.write");

  function _recapBody() {
    const b = { locale: reportLocale };
    if (recapSince) b.since = new Date(recapSince).toISOString();
    if (recapUntil) b.until = new Date(recapUntil).toISOString();
    return b;
  }

  async function generateRecap() {
    setRecapLoading(true);
    try {
      const r = await api.post("/reports/weekly-recap", _recapBody(),
        { params: { format: "html" }, responseType: "text",
          transformResponse: [(d) => d] });
      setRecapHtml(typeof r.data === "string" ? r.data : String(r.data));
    } catch (e) {
      toast.error(t("reports.generate_failed"));
    } finally {
      setRecapLoading(false);
    }
  }

  async function downloadPdf() {
    setPdfLoading(true);
    setPdfStatus("rendering");
    try {
      const r = await api.post("/reports/weekly-recap", _recapBody(),
        { params: { format: "pdf" }, responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "weekly-recap.pdf";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Phase 18c — the recap PDF now also lands in AssetStorage. Poll
      // the status endpoint so the pill flips to ‘ready’ with the honest
      // asset_url from Mongo. This is the sync-fallback path — the poll
      // returns ready immediately because the render is inline.
      setPdfStatus("ready");
      try {
        const s = await api.get("/reports/weekly-recap/pdf/status", {
          params: {
            since: recapSince ? new Date(recapSince).toISOString()
                                    : new Date(Date.now() - 7*24*3600*1000).toISOString(),
            until: recapUntil ? new Date(recapUntil).toISOString()
                                    : new Date().toISOString(),
          }});
        if (s.data?.asset_url) setPdfAssetUrl(s.data.asset_url);
      } catch (_) { /* non-fatal — download still worked inline */ }
    } catch (e) {
      setPdfStatus("failed");
      if (e?.response?.status === 501) {
        toast.error(t("reports.recap_pdf_unavailable"));
      } else {
        toast.error(t("reports.generate_failed"));
      }
    } finally {
      setPdfLoading(false);
    }
  }

  async function sendEmail() {
    const recipients = emailTo.split(",").map((s) => s.trim()).filter(Boolean);
    if (recipients.length === 0) {
      toast.error(t("reports.email_none"));
      return;
    }
    setEmailSending(true);
    try {
      const body = { to: recipients, locale: reportLocale };
      if (recapSince) body.since = new Date(recapSince).toISOString();
      if (recapUntil) body.until = new Date(recapUntil).toISOString();
      await api.post("/reports/weekly-recap/send", body);
      toast.success(t("reports.email_sent"));
      setEmailOpen(false);
      setEmailTo("");
    } catch (e) {
      toast.error(t("reports.email_send_failed"));
    } finally {
      setEmailSending(false);
    }
  }

  async function generateExec() {
    setExecLoading(true);
    try {
      const body = { locale: reportLocale };
      if (recapSince) body.since = new Date(recapSince).toISOString();
      if (recapUntil) body.until = new Date(recapUntil).toISOString();
      const r = await api.post("/reports/exec-summary", body);
      setExecText(r.data?.text || "");
      setExecRunId(r.data?.agent_run_id || null);
    } catch (e) {
      toast.error(t("reports.exec_failed"));
    } finally {
      setExecLoading(false);
    }
  }

  async function loadHistory() {
    try {
      const r = await api.get("/reports");
      setHistory(r.data.items || []);
    } catch (e) {
      // Silently ignore — page still usable for read-only analyst.
    }
  }

  useEffect(() => { loadHistory(); }, []);

  async function generate() {
    setGenerating(true); setError(null);
    try {
      const r = await api.post("/reports/generate",
        { report_type: type, locale: reportLocale });
      setActive(r.data.report);
      setActiveRunMeta({ model: r.data.model, cost_usd: r.data.cost_usd,
                          latency_ms: r.data.latency_ms });
      setModalOpen(false);
      await loadHistory();
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else setError(t("reports.generate_failed"));
    } finally {
      setGenerating(false);
    }
  }

  async function openReport(rid) {
    try {
      const r = await api.get(`/reports/${rid}`);
      setActive(r.data);
      setActiveRunMeta(null);
    } catch (e) { /* noop */ }
  }

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("nav.reports")}</h1>
          <p className="text-sm text-muted-foreground">{t("reports.subtitle")}</p>
        </div>
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogTrigger asChild>
            <Button data-testid="reports-generate-btn">{t("reports.generate")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("reports.generate")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">{t("reports.type")}</label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="reports-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REPORT_TYPES.map((rt) => (
                      <SelectItem key={rt} value={rt} data-testid={`reports-type-${rt}`}>
                        {t(`reports.type_${rt}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">{t("reports.locale")}</label>
                <Select value={reportLocale} onValueChange={setReportLocale}>
                  <SelectTrigger data-testid="reports-locale-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="reports-cancel-btn">
                {t("common.cancel")}
              </Button>
              <Button onClick={generate} disabled={generating} data-testid="reports-submit-btn">
                {generating ? t("reports.generating") : t("reports.generate")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {forbidden && (
        <Card data-testid="reports-forbidden">
          <CardContent className="pt-4 text-sm text-muted-foreground">
            {t("reports.read_only")}
          </CardContent>
        </Card>
      )}
      {error && <p className="text-sm text-rose-600" data-testid="reports-error">{error}</p>}

      {/* Phase 14b — Weekly Recap */}
      <Card data-testid="reports-recap-card">
        <CardHeader className="pb-2">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <CardTitle className="text-base">{t("reports.recap_title")}</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {t("reports.recap_subtitle")}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <label className="text-[11px] text-muted-foreground">
                  {t("reports.recap_date_since")}
                </label>
                <Input type="date" value={recapSince}
                  onChange={(e) => setRecapSince(e.target.value)}
                  className="h-8 w-[130px] text-xs"
                  data-testid="reports-recap-since" />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-[11px] text-muted-foreground">
                  {t("reports.recap_date_until")}
                </label>
                <Input type="date" value={recapUntil}
                  onChange={(e) => setRecapUntil(e.target.value)}
                  className="h-8 w-[130px] text-xs"
                  data-testid="reports-recap-until" />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={generateRecap} disabled={recapLoading}
              data-testid="reports-recap-generate-btn">
              {recapLoading ? t("reports.recap_generating") : t("reports.recap_generate")}
            </Button>
            <Button size="sm" variant="outline" onClick={downloadPdf}
              disabled={pdfLoading || !recapHtml}
              data-testid="reports-recap-pdf-btn">
              {pdfLoading ? t("reports.recap_downloading_pdf") : t("reports.recap_download_pdf")}
            </Button>
            {pdfStatus && (
              <span
                data-testid="reports-recap-pdf-status-pill"
                className={
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border " +
                  (pdfStatus === "ready"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900"
                    : pdfStatus === "failed"
                    ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900"
                    : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900")}
              >
                <span
                  className={
                    "inline-block size-1.5 rounded-full " +
                    (pdfStatus === "ready" ? "bg-emerald-500"
                      : pdfStatus === "failed" ? "bg-red-500"
                      : "bg-amber-500 animate-pulse")}
                />
                PDF: {pdfStatus}
              </span>
            )}
            {pdfStatus === "ready" && pdfAssetUrl && (
              <a
                href={pdfAssetUrl}
                data-testid="reports-recap-pdf-asset-link"
                className="text-xs text-primary underline underline-offset-4 hover:no-underline"
                target="_blank" rel="noreferrer"
              >
                signed URL
              </a>
            )}
            {canWrite && (
              <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline"
                    disabled={!recapHtml}
                    data-testid="reports-recap-email-btn">
                    {t("reports.recap_send_email")}
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="reports-email-modal">
                  <DialogHeader>
                    <DialogTitle>{t("reports.email_modal_title")}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground">
                      {t("reports.email_recipients_label")}
                    </label>
                    <Input type="text" value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      placeholder="ceo@example.com, cmo@example.com"
                      data-testid="reports-email-recipients" />
                    <p className="text-[11px] text-muted-foreground">
                      {t("reports.email_recipients_hint")}
                    </p>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEmailOpen(false)}
                      data-testid="reports-email-cancel">
                      {t("common.cancel")}
                    </Button>
                    <Button onClick={sendEmail} disabled={emailSending}
                      data-testid="reports-email-send">
                      {emailSending ? t("reports.email_sending") : t("reports.email_send")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {recapHtml ? (
            <div className="border rounded-md bg-white overflow-hidden"
                 data-testid="reports-recap-html">
              <iframe
                title="weekly-recap"
                srcDoc={recapHtml}
                sandbox=""
                className="w-full h-[520px]"
                data-testid="reports-recap-iframe"
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground"
               data-testid="reports-recap-empty">
              {t("reports.recap_empty")}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Phase 14b — Executive Summary */}
      <Card data-testid="reports-exec-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t("reports.exec_title")}</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {t("reports.exec_subtitle")}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={generateExec} disabled={execLoading}
              data-testid="reports-exec-generate-btn">
              {execLoading ? t("reports.exec_generating") : t("reports.exec_generate")}
            </Button>
            {execRunId && (
              <Badge variant="outline" className="font-mono text-[10px]"
                data-testid="reports-exec-run-id">
                {execRunId.slice(0, 8)}
              </Badge>
            )}
          </div>
          {execText ? (
            <div className="rounded-md border bg-muted/30 p-4 text-sm whitespace-pre-line leading-relaxed"
                 data-testid="reports-exec-text">
              {execText}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground"
               data-testid="reports-exec-empty">
              {t("reports.exec_empty")}
            </p>
          )}
        </CardContent>
      </Card>

      <ReportDetail report={active} runMeta={activeRunMeta} />

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("reports.history")}</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="reports-history-empty">
              {t("reports.history_empty")}
            </p>
          ) : (
            <ul className="divide-y" data-testid="reports-history">
              {history.map((h) => (
                <li key={h.id} className="py-2 flex items-center justify-between text-sm">
                  <div className="space-x-2">
                    <Badge variant="outline" className="font-mono text-[11px]">{h.report_type}</Badge>
                    <span className="text-muted-foreground">{h.period_from?.slice(0, 10)} → {h.period_to?.slice(0, 10)}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => openReport(h.id)}
                    data-testid={`reports-history-open-${h.id}`}>
                    {t("reports.view")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
